import { useRef, useState } from 'react';
import useDialogFocus from '../../hooks/useDialogFocus';
import { formatValue } from './helpers';

/**
 * Set a value by hand: a figure the sources don't publish in a form the job
 * reads, or a correction. It is published at once, with its source recorded.
 */
const EditValueModal = ({ year, spec, row, onSave, onClose, busy, error }) => {
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);
  const [value, setValue] = useState(row?.value ?? row?.proposed?.value ?? '');
  const [sourceRef, setSourceRef] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [note, setNote] = useState('');
  const invalid = !(Number(value) > 0) || sourceRef.trim().length < 3;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (invalid) return;
    onSave({
      value: Number(value),
      source_ref: sourceRef.trim(),
      source_url: sourceUrl.trim() || null,
      note: note.trim() || null,
      expected_updated_at: row?.updated_at || null,
    });
  };

  return (
    <div
      ref={dialogRef}
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="editValueTitle"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="editValueTitle">
              <i className="bi bi-pencil-square me-2"></i>
              {year} · {spec.label}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={busy} aria-label="Close"></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {row?.value !== undefined && row?.value !== null && (
                <p className="mb-3">Current value: <strong>{formatValue(row.value, spec.unit)}</strong></p>
              )}
              <div className="mb-3">
                <label htmlFor="refValue" className="form-label">
                  Value {spec.unit === 'percent' ? '(%)' : '($)'} *
                </label>
                <input
                  id="refValue"
                  type="number"
                  min="0"
                  step="any"
                  className="form-control"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="refSource" className="form-label">Source *</label>
                <input
                  id="refSource"
                  className="form-control"
                  maxLength={300}
                  placeholder="e.g. IRS Notice 2026-xx, Rev. Proc. 2026-24"
                  value={sourceRef}
                  onChange={(e) => setSourceRef(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="refSourceUrl" className="form-label">Source link (optional)</label>
                <input
                  id="refSourceUrl"
                  type="url"
                  className="form-control"
                  placeholder="https://www.irs.gov/..."
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  disabled={busy}
                />
              </div>
              <label htmlFor="refNote" className="form-label">Note (optional)</label>
              <textarea
                id="refNote"
                className="form-control"
                rows="2"
                maxLength={1000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
              />
              <div className="form-text">
                Saving publishes this value straight away: eMoney Build and Client Helper use it within the hour.
              </div>
              {error && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy || invalid}>
                {busy ? 'Saving…' : 'Publish value'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditValueModal;
